import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import type { MenuState, MenuItem } from '../shared/types';
import { menuReducer, MenuAction } from '../shared/menuReducer';
import { fetchMenu, postMenuAction, RemoteMenuState, UnauthorizedError } from '../data/apiClient';
import { useAuth } from './AuthContext';

const POLL_INTERVAL_MS = 20000;

type SetRemoteAction = { type: '__SET_REMOTE'; remote: RemoteMenuState };

const initialState: RemoteMenuState = {
  tabs: [],
  version: 0,
  updatedAt: 0,
};

function stateReducer(state: RemoteMenuState, action: MenuAction | SetRemoteAction): RemoteMenuState {
  if (action.type === '__SET_REMOTE') {
    return action.remote;
  }
  const next = menuReducer(state, action);
  return { ...state, ...next };
}

interface MenuContextValue {
  tabs: MenuState['tabs'];
  getMenuItem: (itemId: string) => MenuItem | undefined;
  addTab: (tabId: string, name: string) => void;
  renameTab: (tabId: string, name: string) => void;
  archiveTab: (tabId: string) => void;
  restoreTab: (tabId: string) => void;
  reorderTabs: (tabIds: string[]) => void;
  addMenuItem: (tabId: string, itemId: string, name: string, price: number, color: string) => void;
  updateMenuItem: (
    itemId: string,
    updates: { name?: string; price?: number; color?: string; tabId?: string }
  ) => void;
  archiveMenuItem: (itemId: string) => void;
  restoreMenuItem: (itemId: string) => void;
  reorderItems: (tabId: string, itemIds: string[]) => void;
}

const MenuContext = createContext<MenuContextValue | null>(null);

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const versionRef = useRef(state.version);
  versionRef.current = state.version;
  const { logout } = useAuth();

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const remote = await fetchMenu();
        if (!cancelled && remote.version >= versionRef.current) {
          dispatch({ type: '__SET_REMOTE', remote });
        }
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          logout();
        }
      }
    }

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchAction = (action: MenuAction) => {
    dispatch(action);
    postMenuAction(action)
      .then((remote) => dispatch({ type: '__SET_REMOTE', remote }))
      .catch(async (err) => {
        if (err instanceof UnauthorizedError) {
          logout();
          return;
        }
        try {
          const remote = await fetchMenu();
          dispatch({ type: '__SET_REMOTE', remote });
        } catch {
          // Still offline — a later poll tick will retry.
        }
      });
  };

  const value = useMemo<MenuContextValue>(
    () => ({
      tabs: state.tabs,
      getMenuItem: (itemId) => {
        for (const tab of state.tabs) {
          const item = tab.items.find((i) => i.id === itemId);
          if (item) return item;
        }
        return undefined;
      },
      addTab: (tabId, name) => dispatchAction({ type: 'ADD_TAB', tabId, name }),
      renameTab: (tabId, name) => dispatchAction({ type: 'RENAME_TAB', tabId, name }),
      archiveTab: (tabId) => dispatchAction({ type: 'ARCHIVE_TAB', tabId }),
      restoreTab: (tabId) => dispatchAction({ type: 'RESTORE_TAB', tabId }),
      reorderTabs: (tabIds) => dispatchAction({ type: 'REORDER_TABS', tabIds }),
      addMenuItem: (tabId, itemId, name, price, color) =>
        dispatchAction({ type: 'ADD_MENU_ITEM', tabId, itemId, name, price, color }),
      updateMenuItem: (itemId, updates) =>
        dispatchAction({ type: 'UPDATE_MENU_ITEM', itemId, ...updates }),
      archiveMenuItem: (itemId) => dispatchAction({ type: 'ARCHIVE_MENU_ITEM', itemId }),
      restoreMenuItem: (itemId) => dispatchAction({ type: 'RESTORE_MENU_ITEM', itemId }),
      reorderItems: (tabId, itemIds) => dispatchAction({ type: 'REORDER_ITEMS', tabId, itemIds }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
};

export function useMenu(): MenuContextValue {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error('useMenu must be used within a MenuProvider');
  return ctx;
}
